/**
 * The layout lab's URL parameters (GEN-001): `?debug=1&seed=abc&size=9&balls=7`.
 *
 * A Phase 1 development surface, not a player-facing route — it is how physics and
 * layouts get looked at without waiting for the daily system (Phase 3). Parsed
 * defensively: a URL is untrusted input like any other (CLAUDE.md §10).
 *
 * URL-001 (owner, 2026-09-25): a player's address bar is just the site, like Wordle's.
 * Without `debug=1` the table parameters are ignored entirely, and nothing the app does
 * writes a seed into the URL. Only the owner's debug mode deals a table from the URL.
 */
import { dealForSeed, MAX_BALLS_FOR_SIZE, type TableSizeFt } from '@potted/sim';

export interface LabParams {
  /** `?debug=1`: the only mode in which the URL chooses the table. */
  readonly debug: boolean;
  readonly seed: string;
  readonly sizeFt: TableSizeFt;
  readonly ballCount: number;
  /** True when the seed was generated rather than supplied. */
  readonly seedWasGenerated: boolean;
  /**
   * Par, when the link came from the Phase 2 report. Display only: it decides nothing,
   * and a player editing it only changes what their own screen says (N3).
   */
  readonly par: number | undefined;
}

const SIZES: readonly TableSizeFt[] = [7, 8, 9];
const MIN_BALLS = 3;
const MAX_BALLS = 10;

function clampBalls(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const value = Number(raw);
  if (!Number.isInteger(value)) return undefined;
  return value < MIN_BALLS ? MIN_BALLS : value > MAX_BALLS ? MAX_BALLS : value;
}

function readSize(raw: string | null): TableSizeFt | undefined {
  if (raw === null) return undefined;
  const value = Number(raw);
  return SIZES.find((size) => size === value);
}

/**
 * Read the lab parameters from a query string, falling back to a seed derived from
 * the current time when none is given — so opening the page plain still deals a table.
 * Outside debug mode every table parameter is dropped before it is read (URL-001).
 *
 * `allowDebug` is `import.meta.env.DEV` in the page: debug mode exists only in local
 * development, and the build Cloudflare serves ignores `?debug=1` entirely (D-031).
 * Required, not defaulted, so no caller can switch it on by forgetting it (L40).
 */
export function readLabParams(search: string, nowMs: number, allowDebug: boolean): LabParams {
  const all = new URLSearchParams(search);
  const debug = allowDebug && all.get('debug') === '1';
  const params = debug ? all : new URLSearchParams();

  const seedParam = params.get('seed');
  const seed = seedParam !== null && seedParam !== '' ? seedParam : `lab-${nowMs.toString(36)}`;

  /*
   * GEN-002: the seed deals the size and the ball count, as the daily game will.
   * `?size=` and `?balls=` still pin them, for testing one kind of table; a pinned
   * count is capped for the size, so the generator is never asked to overcrowd one.
   */
  const deal = dealForSeed(seed);
  const sizeFt = readSize(params.get('size')) ?? deal.sizeFt;
  const pinned = clampBalls(params.get('balls'));
  const cap = MAX_BALLS_FOR_SIZE[sizeFt];
  const ballCount =
    pinned !== undefined
      ? pinned > cap
        ? cap
        : pinned
      : deal.ballCount > cap
        ? cap
        : deal.ballCount;

  const parParam = params.get('par');
  const parValue = parParam === null || parParam === '' ? Number.NaN : Number(parParam);
  const par = Number.isInteger(parValue) && parValue >= 1 && parValue <= 30 ? parValue : undefined;

  return {
    debug,
    seed,
    seedWasGenerated: seedParam === null || seedParam === '',
    sizeFt,
    ballCount,
    par,
  };
}

/**
 * Where debug mode's "New table" goes: a fresh seed and nothing else, so the new seed
 * deals its own size and balls. Carrying `size` and `balls` over is what kept every new
 * table the same size (GEN-002). New table exists only in debug mode (URL-001), and the
 * flag must survive, or the seed would be ignored on arrival.
 */
export function newTableUrl(seed: string, base: string): string {
  const url = new URL(base);
  url.search = '';
  url.searchParams.set('debug', '1');
  url.searchParams.set('seed', seed);
  return url.toString();
}

/**
 * The debug panel's "new game" (DBG-002): a fresh seed, with the size and ball count
 * the owner chose pinned rather than dealt. Stays in debug mode. The count is clamped
 * again by `readLabParams` on load, so a hand-edited URL cannot overcrowd a table.
 */
export function newGameUrl(
  seed: string,
  sizeFt: TableSizeFt,
  ballCount: number,
  base: string,
): string {
  const url = new URL(base);
  url.search = '';
  url.searchParams.set('debug', '1');
  url.searchParams.set('seed', seed);
  url.searchParams.set('size', String(sizeFt));
  url.searchParams.set('balls', String(ballCount));
  return url.toString();
}

/**
 * The link the results screen shares. A player's is the bare site, like Wordle's: no
 * seed, no day (URL-001). In debug mode it reproduces this exact table, par included,
 * so the owner can send one to themselves or a tester.
 */
export function shareUrl(params: LabParams, base: string): string {
  const url = new URL(base);
  url.search = '';
  url.hash = '';
  if (!params.debug) return url.toString();
  url.searchParams.set('debug', '1');
  url.searchParams.set('seed', params.seed);
  url.searchParams.set('size', String(params.sizeFt));
  url.searchParams.set('balls', String(params.ballCount));
  if (params.par !== undefined) url.searchParams.set('par', String(params.par));
  return url.toString();
}
